import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import remove from 'lodash/remove';
import PropTypes from 'prop-types';

import { Badge, Box, SvgIcon, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AsyncTableComponent from '../../Table/AsyncTableContainer';
import BreadcrumbsContainer from '../containers/BreadcrumbsContainer';

import { CircularLoaderComponent } from '../../LoadingSpinner/LoadingSpinnerComponent';
import ModalHandlerComponent from '../../Modal/ModalHandlerComponent';
import SearchComponent from '../../Table/SearchComponent';
import CreateFolderFormComponent from './CreateFolderFormComponent';
import UploadFileFormComponent from './UploadFileFormComponent';

import {
  getSelectValue,
  isAdmin,
  isBuildingPM,
  isPM,
  isSuperOrConcierge,
} from '../../../utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../../Table/constants';

import { folderIcon, imgIcon, pdfIcon } from '../../../res/svg/icons';
import { TABLE_DATA_TYPES } from '../../../staticData';
import { ButtonLink } from '../../Button';
import { CUSTOM } from '../../Modal/modalTypes';
import StyledSelect from '../../Select';
import ListActions from '../../Table/ListActions';

const IconContainer = ({ icon }) => (
  <Box display="flex" mr="1rem">
    <SvgIcon viewBox="0 0 32 32" fontSize="medium">
      {icon}
    </SvgIcon>
  </Box>
);
IconContainer.propTypes = {
  icon: PropTypes.element.isRequired,
};

const DocumentLibraryComponent = (props) => {
  const theme = useTheme();
  const {
    selectedValue,
    selectOptions,
    onSelectChange,
    onSearchChange,
    onLoad,
    showModalFolder,
    showModalFile,
    handleClick,
    onDeleteItem,
    directoryData,
    breadcrumbs,
    inputValue,
    items,
    getItems,
    listParams,
    listKey,
    form,
    onSubmit,
    isModalShown,
    closeModal,
    fileUrl,
    fileName,
    fileType,
    isModalViewImg,
    isModalFolder,
    isModalFile,
    user,
    isSearing,
    isEditAction,
    initialValues,
    hasPreselected,
    isLoading,
  } = props;

  const directoryInfo = {};
  !isEmpty(directoryData) && directoryData.isPmcDirectory
    ? (directoryInfo.PMCId = directoryData.property)
    : (directoryInfo.buildingId = directoryData.property);

  const isForPmcPmOnly = directoryData.isPmcDirectory
    ? get(user, 'pmcRoles', []).filter(
        (pmc) => pmc.propertyId === directoryData.property,
      )?.length > 0
    : !isEmpty(
        find(
          user?.properties ?? [],
          (property) => property.id === directoryData.property,
        ),
      );

  const isForBuildingPmOnly = !isEmpty(
    find(
      user?.properties,
      (property) => property.id === directoryData.property,
    ),
  );
  const hasActionPermissionsInFolder =
    isAdmin() ||
    (isPM(directoryInfo) && !isBuildingPM(directoryInfo) && isForPmcPmOnly) ||
    (isBuildingPM(directoryInfo) &&
      !directoryData.isPmcDirectory &&
      isForBuildingPmOnly);

  const customActions = breadcrumbs && breadcrumbs.length > 0 && (
    <BreadcrumbsContainer breadcrumbs={breadcrumbs} />
  );

  const columns = [
    {
      Header: 'Name',
      id: 'name',
      headerClassName: 'sort',
      filter: true,
      minWidth: 150,
      accessor: (entry) => {
        const numberOfChildDocuments = get(entry, 'childDocuments', null);
        const name = get(entry, 'name', '');
        const type = get(entry, 'type', '');
        let icon;
        let isBadgeUsed;
        if (name === '...') {
          icon = null;
        } else if (type.includes('directory')) {
          icon = <IconContainer icon={folderIcon} />;
          isBadgeUsed = true;
        } else if (type.includes('image')) {
          icon = <IconContainer icon={imgIcon} />;
        } else if (type.includes('pdf')) {
          icon = <IconContainer icon={pdfIcon} />;
        } else {
          icon = null;
        }
        const isBadgeShown =
          numberOfChildDocuments && numberOfChildDocuments > 0 && isBadgeUsed;
        return (
          <Box display="flex" alignItems="center">
            {icon}
            <ButtonLink
              id={entry.id}
              name={entry.name}
              data-type={entry.type}
              data-url={entry.url}
              onClick={handleClick}
              style={{ color: 'inherit' }}
            >
              {entry.name}
            </ButtonLink>
            {isBadgeShown ? (
              <Box ml="2rem">
                <Badge
                  color="primary"
                  badgeContent={numberOfChildDocuments}
                  max={999}
                  component="div"
                  style={{ verticalAlign: 'super' }}
                />
              </Box>
            ) : null}
          </Box>
        );
      },
    },
    {
      Header: 'Actions',
      id: 'actions',
      fixed: 'right',
      sortable: false,
      label: 'Actions',
      width: 120,
      show: !isSearing,
      accessor: (entry) => {
        let actions = [];
        const removeActions = [
          {
            label: 'Remove',
            color: theme.palette.secondary.main,
            onClick: () => onDeleteItem(entry),
          },
        ];
        const editActions = {
          label: 'Edit',
          color: theme.palette.grey[500],
          onClick: () => {
            showModalFolder(entry?.id, true);
          },
        };
        if (hasActionPermissionsInFolder) {
          actions = actions.concat(removeActions);
          entry?.type === 'directory' && actions.push(editActions);
        } else if (entry?.creator === user?.id) {
          actions = actions.concat(removeActions);
          entry?.type === 'directory' && actions.push(editActions);
        }
        return entry?.name === '...' || entry?.default ? null : (
          <Box width="100%">
            <ListActions actions={actions} id={entry?.id} isFullWidth />
          </Box>
        );
      },
    },
  ];

  let actions = [];
  const folderID = get(directoryData, 'id', null);
  const directoryName = get(directoryData, 'name', null);
  const folderActions = {
    label: 'Create new Folder',
    onClick: () => (folderID !== null ? showModalFolder(folderID) : {}),
  };
  const imageActions = {
    label: 'Upload new Image or File',
    onClick: () => (folderID !== null ? showModalFile(folderID) : {}),
  };

  !isEmpty(directoryData) &&
    (hasActionPermissionsInFolder || isSuperOrConcierge(directoryInfo)) &&
    !directoryData.isRoot &&
    actions.push(folderActions, imageActions);

  if (
    !directoryData.subdirectoriesAllowed ||
    isSuperOrConcierge(directoryInfo)
  ) {
    remove(actions, (action) => action.label === 'Create new Folder');
  }

  if (directoryName === 'Groups') {
    actions = [];
  }

  if (hasPreselected) {
    listParams.building = '';
  }

  const action = <ListActions actions={actions} />;

  let modalContent;
  let title = '';
  if (isModalViewImg) {
    const isImg = fileType.indexOf('image') !== -1;
    title = isImg ? 'Image Preview' : 'File Preview';
    modalContent = (
      <Box
        display="flex"
        justifyContent="center"
        style={{ cursor: 'pointer' }}
        onClick={() => window.open(fileUrl)}
      >
        {isImg ? (
          <Box maxWidth="60rem">
            <img alt={fileName} src={fileUrl} style={{ width: '100%' }} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <SvgIcon viewBox="0 0 32 32" fontSize="large">
              {pdfIcon}
            </SvgIcon>
            <Typography>{fileName}</Typography>
          </Box>
        )}
      </Box>
    );
  } else if (isModalFolder && !isModalFile) {
    title = isEditAction ? 'Edit Folder' : 'Create new Folder';
    modalContent =
      isEditAction && !initialValues?.id ? (
        <CircularLoaderComponent />
      ) : (
        <CreateFolderFormComponent
          form={form}
          onSubmit={onSubmit}
          initialValues={
            isEditAction
              ? { pm: true, pmcOwner: true, ...initialValues }
              : { pm: true, pmcOwner: true }
          }
          isEditAction={isEditAction}
        />
      );
  } else {
    title = 'Upload new Image or File';
    modalContent = <UploadFileFormComponent form={form} onSubmit={onSubmit} />;
  }

  const modalProps = {
    modal: {
      modalType: CUSTOM,
      modalProps: {
        title,
      },
    },
    onModalClose: closeModal,
  };

  const defaultParams = {};
  const tableDataType = TABLE_DATA_TYPES.items;
  return (
    <>
      <Box
        display="flex"
        gap="1rem"
        alignItems="center"
        flexWrap="wrap"
        sx={{
          flexDirection: { xs: 'column' },
        }}
      >
        {!hasPreselected && (
          <Box width="100%">
            <StyledSelect
              value={getSelectValue(selectedValue, selectOptions)}
              onChange={onSelectChange}
              options={selectOptions}
            />
          </Box>
        )}

        <SearchComponent
          placeholder="Search..."
          value={inputValue}
          onChange={onSearchChange}
          disabled={isLoading}
          fullWidth
        />

        {actions?.length ? (
          <Box
            sx={{ alignSelf: 'flex-end', width: { xs: '100%', md: 'auto' } }}
          >
            {action}
          </Box>
        ) : null}
      </Box>
      {customActions ? <Box mt="1rem">{customActions}</Box> : null}
      <AsyncTableComponent
        tableId={TABLE_ID.documentLibraryTable}
        title="Document Library"
        columns={columns}
        defaultSort={TABLE_CONSTANTS.name}
        manual
        showFiltersAreApplied={false}
        items={items}
        onLoad={onLoad}
        getItems={getItems}
        listParams={listParams}
        listKey={listKey}
        isDisabled={isModalShown}
        defaultParams={defaultParams}
        tableDataType={tableDataType}
        isHideSearch
      />
      {isModalShown && (
        <ModalHandlerComponent {...modalProps}>
          {modalContent}
        </ModalHandlerComponent>
      )}
    </>
  );
};
DocumentLibraryComponent.defaultProps = {
  selectedValue: null,
  selectOptions: [],
  onSelectChange: () => {},
  onSearchChange: () => {},
  showModalFolder: () => {},
  showModalFile: () => {},
  handleClick: () => {},
  onDeleteItem: () => {},
  directoryData: {},
  breadcrumbs: [],
  inputValue: '',
  items: {},
  getItems: () => {},
  listParams: {},
  listKey: null,
  isModalShown: false,
  closeModal: () => {},
  fileUrl: null,
  fileName: null,
  fileType: null,
  isModalViewImg: false,
  isModalFolder: false,
  isModalFile: false,
  user: {},
  isSearing: null,
  isEditAction: false,
  initialValues: {},
  hasPreselected: false,
};

DocumentLibraryComponent.propTypes = {
  selectedValue: PropTypes.instanceOf(Object),
  selectOptions: PropTypes.instanceOf(Array),
  onSelectChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  onLoad: PropTypes.func.isRequired,
  showModalFolder: PropTypes.func,
  showModalFile: PropTypes.func,
  handleClick: PropTypes.func,
  onDeleteItem: PropTypes.func,
  directoryData: PropTypes.instanceOf(Object),
  breadcrumbs: PropTypes.instanceOf(Array),
  inputValue: PropTypes.string,
  items: PropTypes.instanceOf(Object),
  getItems: PropTypes.func,
  listParams: PropTypes.instanceOf(Object),
  listKey: PropTypes.string,
  form: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isModalShown: PropTypes.bool,
  closeModal: PropTypes.func,
  fileUrl: PropTypes.string,
  fileName: PropTypes.string,
  fileType: PropTypes.string,
  isModalViewImg: PropTypes.bool,
  isModalFolder: PropTypes.bool,
  isModalFile: PropTypes.bool,
  user: PropTypes.instanceOf(Object),
  isSearing: PropTypes.bool,
  isEditAction: PropTypes.bool,
  initialValues: PropTypes.instanceOf(Object),
  hasPreselected: PropTypes.bool,
  isLoading: PropTypes.bool,
};
export default DocumentLibraryComponent;
